import React, { useState, useRef, useEffect } from 'react';
import { useLogto } from '@logto/react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { settingsService, ChatMessage } from '../services/settingsService';

interface ChatPanelProps {
  modelResults: any;
  savedModels?: any[];
  isOpen: boolean;
  onToggle: () => void;
  initialMessage?: string;  // Optional: Message to send when opening (or if already open)
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  modelResults,
  savedModels = [],
  isOpen,
  onToggle,
  initialMessage,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content:
        'Hi! 👋 I\'m your Marketing Mix Model analyst. I can help you understand your model results, explain metrics, and compare different models. What would you like to know?',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [showModelComparison, setShowModelComparison] = useState(true);
  const [lastProcessedMessage, setLastProcessedMessage] = useState<string | undefined>(undefined);
  const logto = useLogto();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Constants for message management
  const MAX_MESSAGES = 50; // Maximum messages to keep in memory
  const MAX_API_HISTORY = 10; // Maximum history to send to API

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle message when user clicks to explain something
  useEffect(() => {
    // Only process if message is new
    if (!initialMessage || initialMessage === lastProcessedMessage) {
      return;
    }

    if (isOpen) {
      // Chat is already open - set the input and auto-send
      setInput(initialMessage);
      setLastProcessedMessage(initialMessage);

      // Auto-send after the input is set
      const timer = setTimeout(() => {
        const form = document.querySelector('form');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true }));
        }
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // Chat is closed - just prepare the input for when it opens
      setInput(initialMessage);
      setLastProcessedMessage(initialMessage);
    }
  }, [isOpen, initialMessage, lastProcessedMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim()) {
      return;
    }

    const userMessage = input.trim();
    setInput('');

    // Add user message to chat (with size limit)
    setMessages((prev) => {
      const updated = [...prev, { role: 'user' as const, content: userMessage }];
      // Keep only last MAX_MESSAGES to prevent memory leaks
      return updated.slice(-MAX_MESSAGES);
    });
    setLoading(true);

    try {
      // Prepare chat history - limit to MAX_API_HISTORY for token efficiency
      const chatHistoryForAPI = messages.slice(-MAX_API_HISTORY);

      // Check if this is a comparison request
      if (selectedModels.length > 0) {
        // Load selected models and compare
        const modelsToCompare = selectedModels
          .map((modelId) => savedModels.find((m) => m.id === modelId))
          .filter(Boolean);

        const modelNames = modelsToCompare.map((m) => m.name);

        const result = await settingsService.compareModels(
          logto,
          modelsToCompare,
          modelNames,
          userMessage,
          chatHistoryForAPI
        );

        if (result.success && result.response) {
          setMessages((prev) => {
            const updated = [...prev, { role: 'assistant' as const, content: result.response! }];
            return updated.slice(-MAX_MESSAGES);
          });
        } else {
          setMessages((prev) => {
            const updated = [
              ...prev,
              {
                role: 'assistant' as const,
                content: `Sorry, I encountered an error: ${result.message || 'Unknown error'}`,
              },
            ];
            return updated.slice(-MAX_MESSAGES);
          });
        }
      } else {
        // Single model chat
        const result = await settingsService.chatAboutModel(
          logto,
          userMessage,
          modelResults,
          chatHistoryForAPI
        );

        if (result.success && result.response) {
          setMessages((prev) => {
            const updated = [...prev, { role: 'assistant' as const, content: result.response! }];
            return updated.slice(-MAX_MESSAGES);
          });
        } else {
          setMessages((prev) => {
            const updated = [
              ...prev,
              {
                role: 'assistant' as const,
                content: `Sorry, I encountered an error: ${result.message || 'Unknown error'}`,
              },
            ];
            return updated.slice(-MAX_MESSAGES);
          });
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error occurred';
      setMessages((prev) => {
        const updated = [
          ...prev,
          { role: 'assistant' as const, content: `Error: ${errorMsg}` },
        ];
        return updated.slice(-MAX_MESSAGES);
      });
    } finally {
      setLoading(false);
      setTimeout(scrollToBottom, 100);
    }
  };

  const handleModelSelection = (modelId: string) => {
    setSelectedModels((prev) =>
      prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId]
    );
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end">
      {/* Chat Panel */}
      {isOpen && (
        <div className="bg-white rounded-lg shadow-2xl w-96 h-96 flex flex-col mb-4 border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg flex justify-between items-center">
            <h3 className="font-semibold flex items-center gap-2">
              💬 Model Analyst
            </h3>
            <button
              onClick={onToggle}
              className="text-white hover:bg-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-lg"
            >
              −
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xl px-4 py-3 rounded-lg ${msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
                    }`}
                  style={{ maxWidth: msg.role === 'user' ? '400px' : '500px' }}
                >
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none
                      prose-headings:font-bold prose-headings:mt-3 prose-headings:mb-2
                      prose-h1:text-lg prose-h2:text-base prose-h3:text-sm
                      prose-p:m-0 prose-p:mb-2 prose-p:leading-relaxed
                      prose-strong:font-semibold
                      prose-li:m-0 prose-li:p-0 prose-li:mb-1
                      prose-ul:m-0 prose-ul:p-0 prose-ul:pl-4
                      prose-ol:m-0 prose-ol:p-0 prose-ol:pl-4
                      prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:text-red-600
                      prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-3 prose-pre:rounded prose-pre:text-xs prose-pre:overflow-x-auto
                      prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600
                      prose-a:text-blue-500 prose-a:underline
                      text-sm">
                      <ReactMarkdown
                        rehypePlugins={[rehypeSanitize]}
                        components={{
                          h1: ({ node, ...props }) => <h1 className="text-lg font-bold mt-3 mb-2" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="text-base font-bold mt-2 mb-2" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mt-2 mb-1" {...props} />,
                          p: ({ node, ...props }) => <p className="mb-2 leading-relaxed" {...props} />,
                          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
                          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
                          li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                          code: ({ node, className, ...props }: any) => {
                            const isCodeBlock = className && className.includes('language-');
                            return isCodeBlock ?
                              <code className="block bg-gray-900 text-gray-100 p-3 rounded text-xs font-mono overflow-x-auto my-2" {...props} /> :
                              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono text-red-600" {...props} />;
                          },
                          pre: ({ node, ...props }) => <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto my-2" {...props} />,
                          blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-2" {...props} />,
                          table: ({ node, ...props }) => <table className="border-collapse border border-gray-300 text-xs my-2" {...props} />,
                          th: ({ node, ...props }) => <th className="border border-gray-300 px-2 py-1 bg-gray-100 font-semibold" {...props} />,
                          td: ({ node, ...props }) => <td className="border border-gray-300 px-2 py-1" {...props} />,
                          a: ({ node, ...props }) => (
                            <a className="text-blue-500 underline hover:text-blue-700" target="_blank" rel="noopener noreferrer" {...props} />
                          ),
                          img: () => null, // Block images
                          script: () => null, // Block scripts
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-4 py-2 rounded-lg rounded-bl-none">
                  <div className="flex gap-2 items-center">
                    <span className="text-blue-600 text-sm">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Model Selection (for comparison) */}
          {savedModels.length > 0 && showModelComparison && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 max-h-20 overflow-y-auto flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-700 mb-2">Compare models:</p>
                <div className="flex flex-wrap gap-2">
                  {savedModels.slice(0, 3).map((model) => (
                    <label
                      key={model.id}
                      className="flex items-center gap-1 text-xs cursor-pointer hover:bg-white p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedModels.includes(model.id)}
                        onChange={() => handleModelSelection(model.id)}
                        className="w-3 h-3"
                      />
                      <span className="truncate">{model.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setShowModelComparison(false)}
                className="text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-sm flex-shrink-0 mt-1"
                title="Hide model comparison"
              >
                ✕
              </button>
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={handleSendMessage}
            className="border-t border-gray-200 p-3 bg-white rounded-b-lg"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything..."
                disabled={loading}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {loading ? '...' : '→'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={onToggle}
        className={`rounded-full shadow-lg transition-all ${isOpen
            ? 'w-12 h-12 bg-blue-600 hover:bg-blue-700'
            : 'w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800'
          } text-white flex items-center justify-center text-2xl hover:shadow-xl`}
        title={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? '✕' : '💬'}
      </button>
    </div>
  );
};
